import { SessionStatus, TableInfo, TournamentStatus, UnregisteredReason } from './tournaments/models'
import { lobby } from '@vgw/pok-poker-lobby-client-lib'
import PokerLobbySessionManager from './PokerLobbySessionManager'
import { GameType } from './LobbyManager'
import proto = lobby.protocol
import { CurrencyCode } from './constants'
import { fetchAccounts } from './redux/player/AccountActions'
import { _debounce } from './script-utils'

type PlayerGameSubscription = (games: PlayerGame[]) => void
type PlayerGameSessionSubscription = (info: PlayerGame) => void

export default new (class {
	// Data
	private playerGames:  {[key: string]: PlayerGame} = {}

	// Subscriptions
	private allPlayerGamesSubscriptions: PlayerGameSubscription[] = []
	private singlePlayerGameSubscriptions: {[key: string]:PlayerGameSessionSubscription} = {}

	private updateWalletBalance = _debounce(() => fetchAccounts(true), 1000)

	// Initialization
	private completePlayerGamesInitialization: () => void
	private playerGamesInitialized: Promise<void> = new Promise<void>(function(resolve: () => void) {
		this.completePlayerGamesInitialization = resolve
	}.bind(this))

	constructor() {
		PokerLobbySessionManager.subscribeToPlayerGames(this.handleEvent.bind(this))
	}

	private handleEvent(event: proto.LobbyPlayerGameEvent) {
		if (event instanceof proto.LobbyPlayerGameEvent.Subscribed) {
			this.initialize(event.games)
		} else if (event instanceof proto.LobbyPlayerGameEvent.GameAdded) {
			this.addGame(event.game)
		} else if (event instanceof proto.LobbyPlayerGameEvent.GameUpdated) {
			this.updateGame(event.update)
		} else if (event instanceof proto.LobbyPlayerGameEvent.GameRemoved) {
			this.removeGame(event.gameId)
		} else if (event === proto.LobbyPlayerGameEvent.WalletBalanceUpdated) {
			this.updateWalletBalance()
			return
		}
		this.notifyPlayerGamesChanged()
	}

	private initialize(games: proto.LobbyPlayerGame[]) {
		this.playerGames = {}
		games.forEach((g) => {
			if (g instanceof proto.LobbyPlayerGame.SitAndGo) {
				this.playerGames[g.gameId] = mapFromProtocol(g)
			}
			if (g instanceof proto.LobbyPlayerGame.RingGame) {
				this.playerGames[g.gameId] = mapFromProtocol(g)
			}
			if (g instanceof proto.LobbyPlayerGame.ScheduledTournament) {
				this.playerGames[g.gameId] = mapFromProtocol(g)
			}
		})
		this.completePlayerGamesInitialization()
	}

	private addGame(game: proto.LobbyPlayerGame) {
		this.playerGames[game.gameId] = mapFromProtocol(game)
	}

	private updateGame(update: proto.LobbyPlayerGame.Update) {
		const current = this.playerGames[update.gameId]
		this.playerGames[update.gameId] = applyPlayerGameUpdate(current, update)
	}

	private removeGame(gameId: string) {
		delete this.playerGames[gameId]
	}

	subscribeToPlayerGames(subscription: PlayerGameSubscription) {
		this.allPlayerGamesSubscriptions.push(subscription)
	}

	subscribeToPlayerGameSession(gameId: string, subscription: PlayerGameSessionSubscription): PlayerGame | undefined {
		this.singlePlayerGameSubscriptions[gameId] = subscription
		return Object.keys(this.playerGames).map((k) => {
			return this.playerGames[k]
		}).find((i) => i.id === gameId)
	}

	unsubscribeFromPlayerGame(gameId: string) {
		this.singlePlayerGameSubscriptions[gameId] = undefined
	}

	async getAllPlayerGames(gameType: GameType) {
		await this.playerGamesInitialized
		return Object.keys(this.playerGames).map((k) => {
			return this.playerGames[k]
		}).filter((i) => !!i && i.type === gameType)
	}

	private notifyPlayerGamesChanged() {
		const data = Object.values(this.playerGames)
		this.allPlayerGamesSubscriptions.forEach((sub) => {
			sub(data)
		})
		data.forEach((d) => {
			const sub = this.singlePlayerGameSubscriptions[d.id]
			sub && sub(d)
		})
	}
})

const mapFromProtocol = (from: proto.LobbyPlayerGame): PlayerGame => {
	if (from instanceof proto.LobbyPlayerGame.SitAndGo) {
		return {
			id: from.gameId,
			type: GameType.SitAndGo,
			name: from.name,
			status: mapTournamentStatus(from.tournamentStatus),
			sessionStatus: mapSessionStatus(from.sessionStatus),
			rank: from.rank,
			finalPlayerRank: from.finalPlayerRank,
			remainingPlayerCount: from.remainingPlayerCount,
			unregisteredReason: mapFromUnregisteredReason(from.unregisteredReason),
			tableInfo: mapTableInfo(from.tableInfo)
		} as SitAndGoPlayerGame
	}
	if (from instanceof proto.LobbyPlayerGame.RingGame) {
		return {
			id: from.gameId,
			name: from.name,
			type: GameType.Ring,
			playerId: from.playerId,
			sessionId: from.sessionId,
			currencyCode: mapFromCoinType(from.coinType),
			maxBuyIn: from.maxBuyIn,
			minBuyIn: from.minBuyIn,
			sessionStatus: mapRingGameSessionStatus(from.sessionStatus),
			closedReason: mapRingGameSessionClosedReason(from.closedReason),
		} as RingGamePlayerGame
	}
	if (from instanceof proto.LobbyPlayerGame.ScheduledTournament) {
		return {
			id: from.gameId,
			type: GameType.Tournament,
			name: from.name,
			status: mapTournamentStatus(from.tournamentStatus),
			sessionStatus: mapSessionStatus(from.sessionStatus),
			rank: from.rank,
			finalPlayerRank: from.finalPlayerRank,
			remainingPlayerCount: from.remainingPlayerCount,
			unregisteredReason: mapFromUnregisteredReason(from.unregisteredReason),
			tableId: from.tableId
		} as ScheduledTournamentPlayerGame
	}
}


const mapRingGameSessionStatus = (from: proto.RingGameSessionStatus): RingGameSessionStatus => {
	switch (from) {
		case proto.RingGameSessionStatus.SeatRequested:
			return 'SeatRequested'
		case proto.RingGameSessionStatus.Seated:
			return 'Seated'
		case proto.RingGameSessionStatus.Closed:
			return 'Closed'
	}
}

const mapRingGameSessionClosedReason = (from: proto.SessionClosedReason): RingGameSessionClosedReason => {
	switch (from) {
		case proto.SessionClosedReason.BuyInFailed:
			return 'BuyInFailed'
		case proto.SessionClosedReason.RugCheckFailed:
			return 'RugCheckFailed'
		case proto.SessionClosedReason.IpCollision:
			return 'IpCollision'
		case proto.SessionClosedReason.PlayerLeft:
			return 'PlayerLeft'
		case proto.SessionClosedReason.GameCancelled:
			return 'GameCancelled'
		case proto.SessionClosedReason.BuyInTimeLimitExceeded:
			return 'BuyInTimeLimitExceeded'
		case proto.SessionClosedReason.Inactivity:
			return 'Inactivity'
		case proto.SessionClosedReason.SeatNotAvailable:
			return 'SeatNotAvailable'
		case proto.SessionClosedReason.UnexpectedError:
			return 'UnexpectedError'
	}
}

const applyPlayerGameUpdate = (current: PlayerGame, update: proto.LobbyPlayerGame.Update): PlayerGame => {
	if (update instanceof proto.LobbyPlayerGame.SitAndGo.Update) {
		const sitAndGo = current as SitAndGoPlayerGame
		return {
			...sitAndGo,
			status: mapTournamentStatus(update.tournamentStatus) || sitAndGo.status,
			sessionStatus: mapSessionStatus(update.sessionStatus) || sitAndGo.sessionStatus,
			finalPlayerRank: update.finalPlayerRank || sitAndGo.finalPlayerRank,
			rank: update.rank || sitAndGo.rank,
			remainingPlayerCount: update.remainingPlayerCount >= 0 ? update.remainingPlayerCount : sitAndGo.remainingPlayerCount,
			tableInfo: mapTableInfo(update.tableInfo) || sitAndGo.tableInfo,
			unregisteredReason: mapFromUnregisteredReason(update.unregisteredReason) || sitAndGo.unregisteredReason,
		} as SitAndGoPlayerGame
	}
	if (update instanceof proto.LobbyPlayerGame.RingGame.Update) {
		const ringGame = current as RingGamePlayerGame
		return {
			...ringGame,
			sessionStatus: mapRingGameSessionStatus(update.sessionStatus) || ringGame.sessionStatus,
			closedReason: mapRingGameSessionClosedReason(update.closedReason) || ringGame.closedReason,
		} as RingGamePlayerGame
	}
	if (update instanceof proto.LobbyPlayerGame.ScheduledTournament.Update) {
		const game = current as ScheduledTournamentPlayerGame
		return {
			...game,
			status: mapTournamentStatus(update.tournamentStatus) || game.status,
			sessionStatus: mapSessionStatus(update.sessionStatus) || game.sessionStatus,
			finalPlayerRank: update.finalPlayerRank || game.finalPlayerRank,
			rank: update.rank || game.rank,
			remainingPlayerCount: update.remainingPlayerCount >= 0 ? update.remainingPlayerCount : game.remainingPlayerCount,
			tableId: update.tableId || game.tableId,
			unregisteredReason: mapFromUnregisteredReason(update.unregisteredReason) || game.unregisteredReason,
		} as ScheduledTournamentPlayerGame
	}
}

const mapSessionStatus = (from: proto.SessionStatus): SessionStatus => {
	switch (from) {
		case proto.SessionStatus.Created:
			return 'Created'
		case proto.SessionStatus.Registered:
			return 'Registered'
		case proto.SessionStatus.Playing:
			return 'Playing'
		case proto.SessionStatus.Eliminated:
			return 'Eliminated'
		case proto.SessionStatus.Unregistered:
			return 'Unregistered'
		case proto.SessionStatus.Cancelled:
			return 'Cancelled'
		case null:
			return null
	}
}

const mapTableInfo = (from?: proto.TableInfo): TableInfo => {
	if (!from) return null
	return {
		tableId: from.tableId,
		tableAuthUrl: from.tableAuthUrl,
		tableServerInstanceId: from.tableServerInstanceId,
		tableSocketUrl: from.tableSocketUrl,
	}
}

const mapTournamentStatus = (from: proto.TournamentStatus): TournamentStatus => {
	switch (from) {
		case proto.TournamentStatus.Announcing:
			return 'Announced'
		case proto.TournamentStatus.Registering:
			return 'Registering'
		case proto.TournamentStatus.Running:
			return 'Running'
		case proto.TournamentStatus.Finished:
			return 'Finished'
		case proto.TournamentStatus.Cancelled:
			return 'Cancelled'
		case proto.TournamentStatus.OnBreak:
			return 'OnBreak'
		case proto.TournamentStatus.PreparingBreak:
			return 'PreparingBreak'
	}
}

const mapFromUnregisteredReason = (from: proto.UnregisteredReason): UnregisteredReason => {
	switch (from) {
		case proto.UnregisteredReason.PlayerUnregistered:
			return 'PlayerUnregistered'
		case proto.UnregisteredReason.BuyInFailed:
			return 'BuyInFailed'
		case proto.UnregisteredReason.IPCollision:
			return 'IPCollision'
		case proto.UnregisteredReason.RugCheckFailed:
			return 'RugCheckFailed'
	}
}

export type RingGameSessionStatus = 'SeatRequested' | 'Seated' | 'Closed'
export type RingGameSessionClosedReason = 'RugCheckFailed' | 'IpCollision' | 'BuyInFailed' | 'PlayerLeft' | 'GameCancelled' | 'BuyInTimeLimitExceeded' | 'Inactivity' | 'SeatNotAvailable' | 'UnexpectedError'

export type PlayerGame = {
	id: number | string;
	name: string;
	type: GameType;
};

export type SitAndGoPlayerGame = PlayerGame & {
	status: string
	sessionStatus: SessionStatus
	finalPlayerRank: number | undefined
	rank: number | undefined
	remainingPlayerCount: number
	tableInfo: TableInfo | undefined
	unregisteredReason: string | undefined
}

export type ScheduledTournamentPlayerGame = PlayerGame & {
	status: string
	sessionStatus: SessionStatus
	finalPlayerRank: number | undefined
	rank: number | undefined
	remainingPlayerCount: number
	tableId: string | undefined
	unregisteredReason: string | undefined
}

export type RingGamePlayerGame = PlayerGame & {
	playerId: number,
	sessionId: string,
	currencyCode: string,
	maxBuyIn: string,
	minBuyIn: string,
	sessionStatus: RingGameSessionStatus,
	closedReason?: RingGameSessionClosedReason,
}

const mapFromCoinType = (from: proto.CoinType): string => {
	switch (from) {
		case proto.CoinType.Gold:
			return CurrencyCode.GOLD_COINS
		case proto.CoinType.Sweeps:
			return CurrencyCode.SWEEPS
	}
}
